import {
  PRODUCT_PDP_COLOR_CAROUSEL,
  PRODUCT_PDP_DESKTOP,
  PRODUCT_PDP_MOBILE,
  PRODUCT_PDP_MOBILE_PRESCRIPTION,
  PRODUCT_TILE_DESKTOP,
  PRODUCT_TILE_MOBILE,
  PRODUCT_TILE_SWATCHES,
} from '../constants/images-sizes'
import { DESKTOP, MOBILE, TABLET } from '../constants/media-query'
import { Akamai } from './akamai'
import LazyLoad from 'vanilla-lazyload'
import { LIGHT_GRAY } from '@/scripts/constants/colours'
import { GIFTCARDS } from '@/scripts/constants/store'
import WcsImages from '@/scripts/libs/wcs/images'
import makeDebug from 'debug'
import { ImageGallery } from '@/types/pdp'
import {
  SMALL_IMAGE_WHITE_BACKGROUND,
  PRODUCT_IMAGE_MINICART,
} from '@/scripts/constants/images-sizes'
import wcs from '@/scripts/libs/wcs'
import { ALTERNATIVE, FULLWIDTH, KIT, ON_MODEL } from '@/scripts/constants/pdp'
const VIDEO_EXTENSIONS = ['.mpg', '.mp2', '.mpeg', '.mpe', '.mpv', '.mp4']

const debug = makeDebug('images')
class Images {
  private increaseDpi = (pixelSize: number) => {
    const deviceRatio = window.devicePixelRatio
    const multiplier = deviceRatio >= 2 ? 2 : 1
    return pixelSize * multiplier
  }

  get3dProductImage = (mediaQuery: string, productImage: string, backgroundColor: string) => {
    const size = mediaQuery === DESKTOP ? PRODUCT_PDP_DESKTOP.size : PRODUCT_PDP_MOBILE.size
    const dpi = this.increaseDpi(size)
    return Akamai.addParams({ image: productImage, width: dpi, color: backgroundColor })
  }

  getEmailMeImage = (productImage: string) => {
    return Akamai.addParams({
      image: productImage,
      width: this.increaseDpi(PRODUCT_PDP_COLOR_CAROUSEL.size),
      color: PRODUCT_PDP_COLOR_CAROUSEL.color,
    })
  }

  isVideo(src: string): boolean {
    return VIDEO_EXTENSIONS.includes(src)
  }

  getProductImageForPdp = ({
    mediaQuery,
    productImage,
    view,
  }: {
    mediaQuery: string
    productImage: string
    view: string
  }) => {
    if (this.isVideo(productImage)) {
      return productImage.split('?')[0]
    }
    if (view === ON_MODEL || view === FULLWIDTH) {
      return productImage
    }
    const width = mediaQuery === MOBILE ? PRODUCT_PDP_MOBILE.size : PRODUCT_PDP_DESKTOP.size
    const color = mediaQuery === MOBILE ? PRODUCT_PDP_MOBILE.color : PRODUCT_PDP_DESKTOP.color
    return Akamai.addParams({ image: productImage, width: this.increaseDpi(width), color })
  }

  getZoomedImageForPdpCarousel = (mediaQuery: string, productImage: string) => {
    const width = [MOBILE, TABLET].includes(mediaQuery)
      ? PRODUCT_PDP_MOBILE.size
      : PRODUCT_PDP_DESKTOP.size
    return Akamai.addParams({
      image: productImage,
      width: this.increaseDpi(width * 1.5),
      color: PRODUCT_PDP_DESKTOP.color,
    })
  }

  getColorCarouselImage = ({ imageUrl }: { imageUrl: string; productType?: string }) => {
    return Akamai.addParams({
      image: imageUrl,
      width: this.increaseDpi(PRODUCT_PDP_COLOR_CAROUSEL.size),
      color: PRODUCT_PDP_COLOR_CAROUSEL.color,
      impolicy: imageUrl?.includes('crossaccessories') ? 'RB_Product' : '',
    })
  }

  getLensPanelImage = (mediaQuery: string, productImage: string) => {
    const width = [MOBILE, TABLET].includes(mediaQuery)
      ? PRODUCT_PDP_MOBILE_PRESCRIPTION.size
      : PRODUCT_PDP_DESKTOP.size
    const color = [MOBILE, TABLET].includes(mediaQuery)
      ? PRODUCT_PDP_MOBILE_PRESCRIPTION.color
      : PRODUCT_PDP_DESKTOP.color
    return Akamai.addParams({ image: productImage, width: this.increaseDpi(width), color })
  }

  getClusterTileImage = ({
    mediaQuery,
    productImage,
    productType,
  }: {
    mediaQuery: string
    productImage: string
    productType: string
  }) => {
    if (mediaQuery === MOBILE) {
      return Akamai.addParams({
        image: productImage,
        width: this.increaseDpi(PRODUCT_TILE_MOBILE.size),
        color: PRODUCT_TILE_MOBILE.color,
        impolicy: productType === KIT ? 'RB_Product_kit' : '',
      })
    } else {
      const imageWidth = this.increaseDpi(PRODUCT_TILE_DESKTOP.size)
      return Akamai.addParams({
        image: productImage,
        width: imageWidth,
        color: PRODUCT_TILE_DESKTOP.color,
        impolicy: productType === KIT ? 'RB_Product_kit' : '',
      })
    }
  }

  getProductTileVariantImage = ({
    mediaQuery,
    productImage,
    productType,
  }: {
    mediaQuery: string
    productImage: string
    productType: string
  }) => {
    if ([MOBILE, TABLET].includes(mediaQuery)) {
      return Akamai.addParams({
        image: productImage,
        width: this.increaseDpi(PRODUCT_TILE_MOBILE.size),
        color: PRODUCT_TILE_MOBILE.color,
        impolicy: productType === KIT ? 'RB_Product_kit' : '',
      })
    } else {
      return Akamai.addParams({
        image: productImage,
        width: PRODUCT_TILE_SWATCHES.size,
        color: PRODUCT_TILE_SWATCHES.color,
        impolicy: productType === KIT ? 'RB_Product_kit' : '',
      })
    }
  }

  lazyLoadImage = (image: HTMLElement, selector = 'img', onSuccess: () => void) => {
    const options = {
      elements_selector: selector,
      callback_loaded: onSuccess,
    }
    LazyLoad.load(image, options)
  }

  getMinicartItemImage = (productType: string, image: string) => {
    if (!productType || !image) {
      return ''
    }
    if (GIFTCARDS.includes(productType)) {
      return image
    }
    return Akamai.addParams({ image, width: PRODUCT_IMAGE_MINICART.size, color: LIGHT_GRAY })
  }

  getVirtualMirrorProductImage = (image: string) => {
    if (!image) {
      return ''
    }

    const { size, color } = SMALL_IMAGE_WHITE_BACKGROUND
    return Akamai.addParams({ image, width: size, color, bypassCors: true })
  }

  fetchPdpImages = async ({
    altText,
    partNumber,
  }: {
    altText: string
    partNumber: string
  }): Promise<ImageGallery[]> => {
    try {
      const { payload } = await WcsImages.getManifest(partNumber)
      debug('manifest.payload', payload)

      const packageType = payload.packageType
      debug('packageType', packageType)
      const pdpImages = payload.images.filter((image) => image.usage === 'PDP Carousel')
      debug('pdp images', pdpImages)
      const { assetMiddleLayerImagesHost } = wcs.getStoreConfig()
      return pdpImages
        .sort((a, b) => a.order - b.order)
        .map((image) => ({
          src: `${assetMiddleLayerImagesHost}${image.path}`,
          alt: `${altText}_${image.view}`,
          view:
            packageType === 'ACCESSORIES' && image.view === ALTERNATIVE ? FULLWIDTH : image.view,
        }))
    } catch (error) {
      return []
    }
  }
}

export default new Images()
